import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Plus, Pencil, Trash2, Power } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { API_URL } from "../../lib/utils";

export const TPAList = ({ account, headers, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", serial: "" });
  const [saving, setSaving] = useState(false);
  const tpas = account.tpas || [];

  const openNew = () => { setForm({ name: "", serial: "" }); setEditing({}); };
  const openEdit = (t) => { setForm({ name: t.name, serial: t.serial || "" }); setEditing(t); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.tpa_id) await axios.put(`${API_URL}/banks/tpas/${editing.tpa_id}`, form, { headers, withCredentials: true });
      else await axios.post(`${API_URL}/banks/${account.account_id}/tpas`, form, { headers, withCredentials: true });
      toast.success(editing.tpa_id ? "TPA atualizado" : "TPA registado");
      setEditing(null); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao guardar TPA"); }
    finally { setSaving(false); }
  };

  const toggle = async (t) => {
    try {
      await axios.put(`${API_URL}/banks/tpas/${t.tpa_id}`, { is_active: !(t.is_active !== false) }, { headers, withCredentials: true });
      toast.success(t.is_active !== false ? "TPA desativado" : "TPA ativado"); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remover o TPA "${t.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/banks/tpas/${t.tpa_id}`, { headers, withCredentials: true });
      toast.success("TPA removido"); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  return (
    <div className="rounded-md border bg-muted/30 p-2 space-y-1.5" data-testid={`tpa-list-${account.account_id}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> TPA ({tpas.length})</p>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={openNew} data-testid={`tpa-add-btn-${account.account_id}`}><Plus className="w-3 h-3 mr-1" /> TPA</Button>
      </div>
      {tpas.length === 0 && <p className="text-xs text-muted-foreground">Nenhum terminal associado</p>}
      {tpas.map((t) => (
        <div key={t.tpa_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`tpa-item-${t.tpa_id}`}>
          <div className="min-w-0">
            <span className={`font-medium ${t.is_active === false ? "line-through text-muted-foreground" : ""}`}>{t.name}</span>
            {t.serial && <span className="text-muted-foreground font-mono ml-1">· {t.serial}</span>}
            {t.is_active === false && <Badge variant="outline" className="ml-1 text-[9px] py-0">inativo</Badge>}
          </div>
          <div className="flex gap-0.5 shrink-0">
            <Button size="icon" variant="ghost" className="h-6 w-6" title="Editar" onClick={() => openEdit(t)} data-testid={`tpa-edit-${t.tpa_id}`}><Pencil className="w-3 h-3" /></Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" title={t.is_active === false ? "Ativar" : "Desativar"} onClick={() => toggle(t)} data-testid={`tpa-toggle-${t.tpa_id}`}><Power className="w-3 h-3" /></Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" title="Remover" onClick={() => remove(t)} data-testid={`tpa-delete-${t.tpa_id}`}><Trash2 className="w-3 h-3" /></Button>
          </div>
        </div>
      ))}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="tpa-dialog">
          <DialogHeader><DialogTitle>{editing?.tpa_id ? "Editar TPA" : "Novo TPA"} — {account.name}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-1"><Label>Nome do TPA *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='Ex.: "TPA Comida"' data-testid="tpa-dialog-name" /></div>
            <div className="space-y-1"><Label>Nº de série / identificador (opcional)</Label><Input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} data-testid="tpa-dialog-serial" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="tpa-dialog-save">{saving ? "A guardar…" : "Guardar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
